import { cn } from "@/lib/cn";
import { BUDGET_SECONDS, PHASES, formatClock } from "./assessment";

/**
 * The ninety-second scale - the page's signature.
 *
 * The product's entire claim is that a complete pre-task assessment fits inside
 * the time a technician actually has, so this shows the five phases and the
 * clock they run against rather than asserting a number.
 *
 * The instrument is borrowed from the subject's own world on purpose. A ladder
 * is set by a ratio, a lanyard by a length, an angle by a scale - this is a trade
 * that reads measurements off tools all day.
 *
 * ── Equal steps, uneven clock ───────────────────────────────────────────────
 *
 * This used to be a to-scale ruler: the axis was linear time, so each phase's
 * bar took its real share of the track and the seconds ticked evenly underneath.
 * It measured well and it read badly. Sign-off is five seconds of ninety, so its
 * bar was about 28px wide and no label survived in it - the last phase was the
 * one step on the ruler with nothing written on it, which reads as a rendering
 * fault rather than as a short step.
 *
 * So the axis is the **sequence** now and the clock is what varies. Every step
 * gets a fifth of the track and can therefore be named.
 *
 * **This is a genuine trade and it is worth being honest about it.** "The scan
 * is a third of the budget" used to be something the reader measured off the
 * bar; now it is something they read - off the `30s` under the step and off the
 * distance from 0:00 to 0:30. Each step therefore carries its own duration, so
 * the number is never more than one glance from the name it belongs to. If this
 * ever goes back to a linear axis, the labels have to go somewhere else first.
 *
 * The duration used to sit inside the step beside the name, and two lines of
 * text in a 100px box is a cramped box - the name stopped being the thing you
 * read first, which for a diagram whose job is naming the five phases is the
 * wrong outcome. Out here it is an annotation against the track: no fill, no
 * pill, tabular figures at 10px, in the same muted ink as the clock it is a
 * measurement alongside.
 *
 * The step and duration rows share one geometry - `flex` with `flex-1` children
 * and the same gap - so a duration is always centred under its own step by
 * construction. The clock marks and the playhead are the only things positioned
 * by offset, because they are the only two that genuinely index into the track.
 *
 * ── A fifth of the track is not 20% of it ───────────────────────────────────
 *
 * The gap between steps is a fixed 3px, not a percentage, so with five steps and
 * four gaps each step is `(100% - 12px) / 5` wide - and its left edge is at
 * `i × (step + 3px)`, which is **not** `i × 20%`. Placing the clock marks at
 * round percentages put the last few ticks up to ten pixels to the right of the
 * step edges they are supposed to mark, which is the kind of drift that reads as
 * sloppiness without ever looking like a bug.
 *
 * So both offsets are `calc()` over the same two numbers the flex rows use. Any
 * change to the gap or the phase count flows through `STEP_W` to the ticks and
 * the playhead together; nothing has to be kept in step by hand.
 */

/** Matches the `gap-[3px]` on `.scale-steps` and `.scale-durations`. */
const GAP_PX = 3;
const STEP_COUNT = PHASES.length;

/** One step's width, as CSS. `(100% - all the gaps) / steps`. */
const STEP_W = `((100% - ${(STEP_COUNT - 1) * GAP_PX}px) / ${STEP_COUNT})`;

/** The left edge of step `index`, or the far end of the track at `STEP_COUNT`. */
function boundaryLeft(index: number): string {
  if (index === 0) return "0%";
  if (index >= STEP_COUNT) return "100%";
  return `calc(${index} * (${STEP_W} + ${GAP_PX}px))`;
}

/**
 * Elapsed seconds → a position on a non-linear axis.
 *
 * The playhead cannot be `elapsed / 90` any more: at 40 seconds the technician
 * is a quarter of the way through step two, not 44% of the way along the track.
 * So it resolves the phase, then interpolates inside that phase's own width.
 */
function playheadLeft(seconds: number): string {
  const index = PHASES.findIndex((phase) => seconds < phase.to);
  if (index === -1) return "100%";

  const phase = PHASES[index]!;
  // Rounded because this lands in a `style` attribute on every replay frame, and
  // seventeen decimal places of `0.1333…` is a string nobody wants in the DOM.
  const within = Math.max(0, (seconds - phase.from) / (phase.to - phase.from)).toFixed(4);
  return `calc(${boundaryLeft(index)} + ${within} * ${STEP_W})`;
}

export function SecondScale({
  /** Where the playhead sits. Omit for a static scale with no head. */
  elapsed,
  tone = "panel",
  animated = false,
  className,
}: {
  elapsed?: number;
  tone?: "panel" | "surface";
  animated?: boolean;
  className?: string;
}) {
  const onPanel = tone === "panel";
  const active = elapsed === undefined ? null : Math.min(elapsed, BUDGET_SECONDS);

  /* Six of them for five steps: every boundary, including both ends. */
  const marks = [...PHASES.map((phase) => phase.from), BUDGET_SECONDS];

  return (
    // `relative` on the root because the playhead spans the steps and the clock.
    // `pb-5` is load-bearing: the figures sit below the mark line and would
    // otherwise overflow the box.
    <div className={cn("scale-root", animated && "scale-root--animated", className)}>
      {/* ── What happens ─────────────────────────────────────────────────── */}
      <div className="scale-steps">
        {PHASES.map((phase) => {
          const done = active !== null && active >= phase.to;
          const current = active !== null && active >= phase.from && active < phase.to;

          return (
            <div
              key={phase.id}
              title={`${phase.label} · ${phase.to - phase.from}s`}
              className={cn(
                "scale-step",
                onPanel && "scale-step--panel",
                current ? "scale-step--current" : done && "scale-step--done"
              )}
            >
              {phase.short}
            </div>
          );
        })}
      </div>

      {/* ── How long ─────────────────────────────────────────────────────────
          Its own row, sharing the steps' geometry so each figure lands centred
          under the step it belongs to. `aria-hidden` because the accessible
          name for each duration is already on the step's `title` above, and a
          screen reader reading five bare numbers in a row says nothing. */}
      <div aria-hidden className={cn("scale-durations", onPanel && "scale-durations--panel")}>
        {PHASES.map((phase) => (
          <span key={phase.id} className="scale-duration">
            {phase.to - phase.from}s
          </span>
        ))}
      </div>

      {/* ── The clock ────────────────────────────────────────────────────────
          One figure per boundary. The spacing is even and the values are not,
          which is the inverse of the ruler this replaced - and the gaps between
          them are where each step's real share of the budget shows up. */}
      <div className="scale-clock">
        <div className={cn("scale-clock__line", onPanel && "scale-clock__line--panel")} />

        {marks.map((second, index) => {
          const passed = active !== null && active >= second;

          return (
            <span key={second} className="scale-mark" style={{ left: boundaryLeft(index) }}>
              <span
                aria-hidden
                className={cn(
                  "scale-mark__tick",
                  onPanel && "scale-mark__tick--panel",
                  passed && "scale-mark__tick--passed"
                )}
              />
              {/* The first and last are pulled inside their own marks so `0:00`
                  and `1:30` do not hang off either edge of the container. */}
              <span
                className={cn(
                  "scale-mark__figure",
                  onPanel && "scale-mark__figure--panel",
                  index === 0 && "scale-mark__figure--first",
                  index === marks.length - 1 && "scale-mark__figure--last"
                )}
              >
                {formatClock(second)}
              </span>
            </span>
          );
        })}
      </div>

      {/* ── Playhead ────────────────────────────────────────────────────────
          Outside the clock row so it runs the full height of the scale - the
          steps are what it is pointing at, and a head that stopped at the mark
          line would look like it belonged to the marks alone. */}
      {active !== null && (
        <span
          aria-hidden
          className={cn("scale-playhead", onPanel && "scale-playhead--panel")}
          style={{ left: playheadLeft(active) }}
        >
          <span className="scale-playhead__dot" />
        </span>
      )}
    </div>
  );
}
